import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import currency from "currency.js";
import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { useFeatureIsOn } from "@/app/featureFlags";
import { Button } from "@/components/Button/Button";
import { PersistentDrawer } from "@/components/Drawer";
import { usePurchaseStateContext } from "@/contexts/PurchaseStateContext";
import { AdditionalInformationCard } from "@/features/trips/components/bookingFlow/tripDetails/AdditionalInformationCard";
import {
  InclusionsCard,
  ExclusionsCard,
} from "@/features/trips/components/bookingFlow/tripDetails/FeaturesCards";
import { HotelsCard } from "@/features/trips/components/bookingFlow/tripDetails/HotelsCard";
import { OverviewCard } from "@/features/trips/components/bookingFlow/tripDetails/OverviewCard";
import { TermsAndConditionsCard } from "@/features/trips/components/bookingFlow/tripDetails/TermsAndConditionsCard";
import { TripTourCard } from "@/features/trips/components/bookingFlow/tripDetails/TripTourCard";
import { TripTourDrawer } from "@/features/trips/components/bookingFlow/tripDetails/TripTourDrawer";
import { TripTourPlanCard } from "@/features/trips/components/bookingFlow/tripDetails/TripTourPlanCard";
import { VisaPermissionsCard } from "@/features/trips/components/bookingFlow/tripDetails/VisaPermissionsCard";
import { tripQueryOptions } from "@/features/trips/hooks/useTrips";
import type { BookingData } from "@/features/trips/types/types";
import { rudderAnalytics, TripTrackingEvents } from "@/lib/rudderstack";
import { formatCurrency } from "@/utils/currency";

export const Route = createFileRoute("/_authenticated/trips/$tripId/")({
  component: RouteComponent,
});

function RouteComponent() {
  const intl = useIntl();
  const { tripId } = Route.useParams();
  const navigate = Route.useNavigate();
  const { data } = useSuspenseQuery(tripQueryOptions(tripId));
  const { updatePurchaseState } = usePurchaseStateContext();
  const { setValue } = useFormContext<BookingData>();

  const [isTripTourDrawerOpen, setTripTourDrawerOpen] = useState(false);

  const isTripTourAdded = useWatch<BookingData, "isTripTourAdded">({
    name: "isTripTourAdded",
  });

  const areOptionalToursEnabled = useFeatureIsOn("optional-tours");

  useEffect(() => {
    rudderAnalytics.track(TripTrackingEvents.USER_VIEWED_TRIP_DETAILS, {
      trip_id: tripId,
      trip_name: data.name,
    });
  }, [tripId, data.name]);

  const handleContinue = () => {
    if (areOptionalToursEnabled && data.hasOptionalTours && !isTripTourAdded) {
      rudderAnalytics.track(
        TripTrackingEvents.USER_VIEWED_TRIP_OPTIONAL_TOURS,
        {
          source: "auto",
          trip_id: data.id,
          trip_name: data.name,
          price: data.tourPrice ?? 0,
        },
      );

      setTripTourDrawerOpen(true);
      return;
    }

    proceedToBooking();
  };

  const proceedToBooking = () => {
    rudderAnalytics.track(
      TripTrackingEvents.USER_PROCEEDED_TO_TRIP_AVAILABILITY,
      {
        trip_id: tripId,
        trip_name: data.name,
      },
    );
    updatePurchaseState("inProgress");
    navigate({ to: "./booking" });
  };

  return (
    <>
      <div className="space-y-3">
        <OverviewCard trip={data} />

        {useFeatureIsOn("optional-tours") && data.hasOptionalTours ? (
          <TripTourCard trip={data} source="details" />
        ) : data.itineraries.length > 0 ? (
          <TripTourPlanCard itineraries={data.itineraries} />
        ) : null}

        <InclusionsCard trip={data} />
        <ExclusionsCard trip={data} />
        <HotelsCard trip={data} />
        <VisaPermissionsCard countries={data.countries} />
        {data.additionalInformation ? (
          <AdditionalInformationCard content={data.additionalInformation} />
        ) : null}
        {data.termsAndConditions ? (
          <TermsAndConditionsCard content={data.termsAndConditions} />
        ) : null}
      </div>

      <PersistentDrawer>
        <dl className="mt-2 mb-4 grid grid-cols-2 gap-2 text-sm text-text-primary">
          <dt>
            {intl.formatMessage({
              defaultMessage: "Total price from:",
              description:
                "Starting total price label displayed on the trip details page",
            })}
          </dt>
          <dd className="text-end font-bold">
            {formatCurrency(
              currency(data.discountedPrice).add(
                isTripTourAdded ? (data.tourPrice ?? 0) : 0,
              ).value,
            )}
          </dd>
        </dl>

        <Button fullWidth variant="contained" onClick={handleContinue}>
          {intl.formatMessage({
            defaultMessage: "Continue",
            description:
              "Button to proceed to the next step in the trip booking flow",
          })}
        </Button>
      </PersistentDrawer>

      {useFeatureIsOn("optional-tours") &&
        data.hasOptionalTours &&
        !isTripTourAdded && (
          <TripTourDrawer
            trip={data}
            open={isTripTourDrawerOpen}
            onClose={() => setTripTourDrawerOpen(false)}
          >
            <div className="w-[45%]">
              <Button fullWidth variant="outlined" onClick={proceedToBooking}>
                {intl.formatMessage({
                  defaultMessage: "Skip",
                  description:
                    "Button to skip optional tour selection and proceed without adding tours",
                })}
              </Button>
            </div>

            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                rudderAnalytics.track(
                  TripTrackingEvents.USER_ADDED_TRIP_OPTIONAL_TOURS,
                  {
                    source: "auto",
                    trip_id: data.id,
                    trip_name: data.name,
                    price: data.tourPrice ?? 0,
                  },
                );

                setValue("isTripTourAdded", true);
                proceedToBooking();
              }}
            >
              {intl.formatMessage({
                defaultMessage: "Add Tours",
                description:
                  "Button to confirm and add selected optional tours to the booking",
              })}
            </Button>
          </TripTourDrawer>
        )}
    </>
  );
}
